/**
 * Worklist Service
 *
 * API client for worklist operations
 */

import { get, post } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  WorklistItem,
  WorklistItemWithPatient,
  WorklistFilters,
  WorklistSearchResult,
  CreateWorklistItemData,
  UpdateWorklistItemData,
} from '@/domain/worklist'
import type { WorklistStatus, PriorityLevel } from '@/config/constants'

// =============================================================================
// Types
// =============================================================================

interface WorklistItemResponse {
  item: WorklistItem
  message?: string
}

interface WorklistItemsResponse {
  items: WorklistItem[]
  total?: number
}

export interface AssignWorklistData {
  assignedTo: string
  priority?: number
  priorityId?: string // UUID reference to priorities table
  dueAt?: string
  notes?: string
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Search/list worklist items with filters
 */
export async function getWorklistItems(filters?: WorklistFilters): Promise<WorklistSearchResult> {
  const params = new URLSearchParams()

  if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo)
  if (filters?.status) params.append('status', filters.status)
  if (filters?.priority) params.append('priority', filters.priority.toString())
  if (filters?.studyId) params.append('studyId', filters.studyId)
  if (filters?.dueBefore) params.append('dueBefore', filters.dueBefore)
  if (filters?.dueAfter) params.append('dueAfter', filters.dueAfter)
  if (filters?.limit) params.append('limit', filters.limit.toString())
  if (filters?.offset) params.append('offset', filters.offset.toString())

  const queryString = params.toString()
  const url = queryString ? `${API_ROUTES.WORKLIST.BASE}?${queryString}` : API_ROUTES.WORKLIST.BASE

  return get<WorklistSearchResult>(url)
}

/**
 * Get a single worklist item by ID
 */
export async function getWorklistItemById(id: string): Promise<WorklistItem> {
  const response = await get<WorklistItemResponse>(API_ROUTES.WORKLIST.BY_ID(id))
  return response.item
}

/**
 * Get my worklist (current user's assigned items)
 */
/**
 * Mi lista de trabajo.
 *
 * Devuelve `WorklistItemWithPatient`, que es lo que el servidor manda de
 * verdad: `findByAssignee` une `studies` y `patients`. El tipo decia
 * `WorklistItem` a secas y por eso la pantalla no podia pintar de que estudio
 * ni de que paciente era cada fila, aunque el dato ya estuviera llegando.
 */
export async function getMyWorklist(status?: WorklistStatus): Promise<WorklistItemWithPatient[]> {
  const url = status
    ? `${API_ROUTES.WORKLIST.MY}?status=${status}`
    : API_ROUTES.WORKLIST.MY
  const response = await get<{ items: WorklistItemWithPatient[] }>(url)
  return response.items
}

/**
 * Get overdue worklist items
 */
export async function getOverdueItems(): Promise<WorklistItem[]> {
  const response = await get<WorklistItemsResponse>(API_ROUTES.WORKLIST.OVERDUE)
  return response.items
}

/**
 * Create a new worklist item
 */
export async function createWorklistItem(data: CreateWorklistItemData): Promise<WorklistItem> {
  const response = await post<WorklistItemResponse>(API_ROUTES.WORKLIST.BASE, data)
  return response.item
}

/**
 * Update a worklist item
 */
export async function updateWorklistItem(
  id: string,
  data: UpdateWorklistItemData
): Promise<WorklistItem> {
  const response = await post<WorklistItemResponse>(API_ROUTES.WORKLIST.UPDATE(id), data)
  return response.item
}

/**
 * Start working on a worklist item
 * Uses dedicated endpoint that only requires worklist:view:own permission
 */
export async function startWorklistItem(id: string): Promise<WorklistItem> {
  const response = await post<WorklistItemResponse>(API_ROUTES.WORKLIST.START(id), {})
  return response.item
}

/**
 * Complete a worklist item
 * Uses dedicated endpoint that only requires worklist:view:own permission
 */
export async function completeWorklistItem(id: string): Promise<WorklistItem> {
  const response = await post<WorklistItemResponse>(API_ROUTES.WORKLIST.COMPLETE(id), {})
  return response.item
}

/**
 * Cancel a worklist item
 * Uses dedicated endpoint that only requires worklist:view:own permission
 */
export async function cancelWorklistItem(id: string): Promise<WorklistItem> {
  const response = await post<WorklistItemResponse>(API_ROUTES.WORKLIST.CANCEL(id), {})
  return response.item
}

/**
 * Delete a worklist item
 */
export async function deleteWorklistItem(id: string): Promise<void> {
  await post(API_ROUTES.WORKLIST.DELETE(id), {})
}

/**
 * Get unassigned worklist items
 * For admin/technician to see what needs assignment
 */
export async function getUnassignedItems(limit = 50, offset = 0): Promise<WorklistItem[]> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  })
  const response = await get<WorklistItemsResponse>(`${API_ROUTES.WORKLIST.UNASSIGNED}?${params}`)
  return response.items
}

/**
 * Assign a worklist item to a radiologist
 */
export async function assignWorklistItem(
  id: string,
  data: AssignWorklistData
): Promise<WorklistItem> {
  const response = await post<WorklistItemResponse>(API_ROUTES.WORKLIST.ASSIGN(id), data)
  return response.item
}

/**
 * Return a worklist item with a reason
 */
export async function returnWorklistItem(
  id: string,
  reason: string
): Promise<WorklistItem> {
  const response = await post<WorklistItemResponse>(API_ROUTES.WORKLIST.RETURN(id), { reason })
  return response.item
}

/**
 * Get returned worklist items
 */
export async function getReturnedItems(): Promise<WorklistItem[]> {
  const response = await get<WorklistItemsResponse>(API_ROUTES.WORKLIST.RETURNED)
  return response.items
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format due date for display
 */
export function formatDueDate(dateString: string | null): string {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Get relative time string
 */
export function getRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffMins = Math.round(diffMs / 60000)
  const diffHours = Math.round(diffMs / 3600000)
  const diffDays = Math.round(diffMs / 86400000)

  if (diffMins < 0) {
    // Past
    const absMins = Math.abs(diffMins)
    const absHours = Math.abs(diffHours)
    const absDays = Math.abs(diffDays)

    if (absMins < 60) return `Hace ${absMins} min`
    if (absHours < 24) return `Hace ${absHours}h`
    return `Hace ${absDays}d`
  } else {
    // Future
    if (diffMins < 60) return `En ${diffMins} min`
    if (diffHours < 24) return `En ${diffHours}h`
    return `En ${diffDays}d`
  }
}

/**
 * Get status badge variant
 */
export function getWorklistStatusVariant(
  status: WorklistStatus
): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'PENDING':
      return 'secondary'
    case 'IN_PROGRESS':
      return 'warning'
    case 'COMPLETED':
      return 'success'
    case 'CANCELLED':
      return 'destructive'
    case 'RETURNED':
      return 'warning'
    default:
      return 'default'
  }
}

/**
 * Get priority badge variant
 */
export function getPriorityVariant(
  priority: PriorityLevel
): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (priority) {
    case 1:
      return 'destructive'
    case 2:
      return 'warning'
    case 3:
      return 'default'
    case 4:
      return 'secondary'
    default:
      return 'default'
  }
}
